/**
 * Historical Cost Dashboard Component
 * Provides long-term cost analysis, trends, and forecasting
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  DatePicker, 
  Select, 
  Button, 
  Statistic, 
  Alert, 
  Spin, 
  Table,
  Tag,
  Tooltip,
  Space,
  Switch,
  Segmented
} from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  ComposedChart
} from 'recharts';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  BarChartOutlined,
  LineChartOutlined,
  CalendarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { monitoringApi } from '../services/monitoring';

const { RangePicker } = DatePicker;
const { Option } = Select;

const HistoricalCostDashboard = () => {
  // State management
  const [historicalData, setHistoricalData] = useState([]);
  const [trendsData, setTrendsData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [dateRange, setDateRange] = useState([
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    new Date()
  ]);
  const [granularity, setGranularity] = useState('daily');
  const [selectedService, setSelectedService] = useState(null);
  const [viewMode, setViewMode] = useState('overview');
  const [showForecast, setShowForecast] = useState(true);
  const [forecastDays, setForecastDays] = useState(30);

  // Services list
  const services = [
    'frame-extractor',
    'solo-analyzer', 
    'facemesh-analyzer',
    'batched-aggregator',
    'web-api',
    'web-client'
  ];

  // Fetch historical cost data
  const fetchHistoricalData = async () => {
    if (!dateRange || dateRange.length !== 2) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [startDate, endDate] = dateRange;
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      
      const [historical, trends, forecast] = await Promise.all([
        monitoringApi.getHistoricalCosts(start, end, granularity),
        monitoringApi.getCostTrends(30, selectedService),
        showForecast ? monitoringApi.getCostForecast(forecastDays, 0.8) : null
      ]);
      
      setHistoricalData(historical.data || []);
      setTrendsData(trends);
      setForecastData(forecast);
      
    } catch (err) {
      setError(`Failed to fetch historical data: ${err.message}`);
      console.error('Error fetching historical cost data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoricalData();
  }, [dateRange, granularity, selectedService, showForecast, forecastDays]);

  // Process data for charts
  const chartData = useMemo(() => {
    if (!historicalData.length) return [];
    
    return historicalData.map(item => ({
      date: granularity === 'hourly' ? 
        new Date(item.hour + ':00:00Z').toLocaleDateString() + ' ' + 
        new Date(item.hour + ':00:00Z').toLocaleTimeString() :
        new Date(item.date).toLocaleDateString(),
      totalCost: item.totalCost || 0,
      videosProcessed: item.videosProcessed || 0,
      costPerVideo: item.costPerVideo || 0,
      throughput: item.throughputVideosPerHour || 0,
      framesAnalyzed: item.framesAnalyzed || 0,
      costPerFrame: item.costPerFrame || 0,
      // Service costs
      frameExtractorCost: item.serviceCosts?.['frame-extractor']?.totalCost || 0,
      soloAnalyzerCost: item.serviceCosts?.['solo-analyzer']?.totalCost || 0,
      facemeshAnalyzerCost: item.serviceCosts?.['facemesh-analyzer']?.totalCost || 0,
      aggregatorCost: item.serviceCosts?.['batched-aggregator']?.totalCost || 0,
      webApiCost: item.serviceCosts?.['web-api']?.totalCost || 0,
      webClientCost: item.serviceCosts?.['web-client']?.totalCost || 0
    }));
  }, [historicalData, granularity]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!chartData.length) return {};
    
    const totalCost = chartData.reduce((sum, item) => sum + item.totalCost, 0);
    const totalVideos = chartData.reduce((sum, item) => sum + item.videosProcessed, 0);
    const avgCostPerVideo = totalVideos > 0 ? totalCost / totalVideos : 0;
    const avgDailyCost = totalCost / chartData.length;
    
    // Trend calculation
    const costs = chartData.map(item => item.totalCost);
    const trend = costs.length > 1 ? 
      ((costs[costs.length - 1] - costs[0]) / costs[0]) * 100 : 0;
    
    return {
      totalCost,
      totalVideos,
      avgCostPerVideo,
      avgDailyCost,
      trend,
      dataPoints: chartData.length
    };
  }, [chartData]);

  // Render trend indicator
  const renderTrendIndicator = (value, suffix = '%') => {
    const isPositive = value > 0;
    const color = isPositive ? '#ff4d4f' : '#52c41a';
    const icon = isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />;
    
    return (
      <span style={{ color }}>
        {icon} {Math.abs(value).toFixed(2)}{suffix}
      </span>
    );
  };

  // Render forecast data
  const renderForecastChart = () => {
    if (!forecastData || !forecastData.forecast.length) return null;
    
    const forecastChartData = forecastData.forecast.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      projected: item.projectedCost,
      lowerBound: item.lowerBound,
      upperBound: item.upperBound
    }));
    
    return (
      <Card title="Cost Forecast" size="small">
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Statistic 
              title="Projected Monthly Cost" 
              value={forecastData.totalProjected} 
              precision={4}
              prefix="$"
            />
            <Statistic 
              title="Trend" 
              value={forecastData.trend}
              valueStyle={{ 
                color: forecastData.trend === 'increasing' ? '#ff4d4f' : '#52c41a' 
              }}
            />
            <Statistic 
              title="Confidence" 
              value={(forecastData.confidence * 100).toFixed(0)}
              suffix="%"
            />
          </Space>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={forecastChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <ChartTooltip formatter={(value) => [`$${value.toFixed(4)}`, 'Cost']} />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="upperBound" 
              stackId="1" 
              stroke="#ff7875" 
              fill="#ff7875" 
              fillOpacity={0.2}
              name="Upper Bound"
            />
            <Area 
              type="monotone" 
              dataKey="projected" 
              stackId="2" 
              stroke="#1890ff" 
              fill="#1890ff" 
              fillOpacity={0.6}
              name="Projected Cost"
            />
            <Area 
              type="monotone" 
              dataKey="lowerBound" 
              stackId="3" 
              stroke="#52c41a" 
              fill="#52c41a" 
              fillOpacity={0.2}
              name="Lower Bound"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // Render service cost breakdown
  const renderServiceCostChart = () => {
    if (!chartData.length) return null;
    
    return (
      <Card title="Service Cost Breakdown Over Time" size="small">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <ChartTooltip formatter={(value) => [`$${value.toFixed(4)}`, 'Cost']} />
            <Legend />
            <Area type="monotone" dataKey="frameExtractorCost" stackId="1" stroke="#1890ff" fill="#1890ff" name="Frame Extractor" />
            <Area type="monotone" dataKey="soloAnalyzerCost" stackId="1" stroke="#52c41a" fill="#52c41a" name="Solo Analyzer" />
            <Area type="monotone" dataKey="facemeshAnalyzerCost" stackId="1" stroke="#faad14" fill="#faad14" name="Facemesh Analyzer" />
            <Area type="monotone" dataKey="aggregatorCost" stackId="1" stroke="#722ed1" fill="#722ed1" name="Aggregator" />
            <Area type="monotone" dataKey="webApiCost" stackId="1" stroke="#13c2c2" fill="#13c2c2" name="Web API" />
            <Area type="monotone" dataKey="webClientCost" stackId="1" stroke="#eb2f96" fill="#eb2f96" name="Web Client" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    );
  };

  // Render trends analysis
  const renderTrendsAnalysis = () => {
    if (!trendsData || trendsData.type === 'error') return null;
    
    if (trendsData.type === 'service_trends') {
      // Service-specific trends
      return (
        <Card title={`${trendsData.service} Cost Trends`} size="small">
          <Table 
            dataSource={trendsData.trends}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Period',
                dataKey: 'period',
                render: (_, record) => record.period || 'N/A'
              },
              {
                title: 'Total Cost',
                dataKey: 'totalCost',
                render: (value) => `$${(value || 0).toFixed(4)}`
              },
              {
                title: 'Frames',
                dataKey: 'frameCount',
                render: (value) => (value || 0).toLocaleString()
              },
              {
                title: 'Avg Processing Time',
                dataKey: 'avgProcessingTime',
                render: (value) => `${(value || 0).toFixed(2)}ms`
              },
              {
                title: 'Efficiency',
                dataKey: 'efficiency',
                render: (value) => (
                  <Tag color={value > 0.7 ? 'green' : value > 0.4 ? 'orange' : 'red'}>
                    {((value || 0) * 100).toFixed(1)}%
                  </Tag>
                )
              }
            ]}
          />
        </Card>
      );
    } else {
      // Overall trends
      const analysis = trendsData.analysis;
      if (!analysis) return null;
      
      return (
        <Card title="Cost Trends Analysis" size="small">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="Trend Direction" 
                value={analysis.trendDirection}
                valueStyle={{ 
                  color: analysis.trendDirection === 'increasing' ? '#ff4d4f' : 
                        analysis.trendDirection === 'decreasing' ? '#52c41a' : '#1890ff'
                }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Avg Daily Cost" 
                value={analysis.avgDailyCost} 
                precision={4}
                prefix="$"
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Cost per Video" 
                value={analysis.avgCostPerVideo} 
                precision={4}
                prefix="$"
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="Next Week Projection" 
                value={analysis.nextWeekProjection} 
                precision={4}
                prefix="$"
              />
            </Col>
          </Row>
          
          {analysis.serviceTrends && (
            <div style={{ marginTop: 16 }}>
              <h4>Service Trends</h4>
              <Space wrap>
                {Object.entries(analysis.serviceTrends).map(([service, trend]) => (
                  <Tag 
                    key={service}
                    color={trend.direction === 'increasing' ? 'red' : 
                          trend.direction === 'decreasing' ? 'green' : 'blue'}
                  >
                    {service}: {trend.direction}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </Card>
      );
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space>
              <span>Date Range:</span>
              <RangePicker 
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <span>Granularity:</span>
              <Select value={granularity} onChange={setGranularity} style={{ width: 100 }}>
                <Option value="daily">Daily</Option>
                <Option value="hourly">Hourly</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <span>Service:</span>
              <Select 
                value={selectedService} 
                onChange={setSelectedService} 
                style={{ width: 150 }}
                allowClear
                placeholder="All Services"
              >
                {services.map(service => (
                  <Option key={service} value={service}>{service}</Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <span>View:</span>
              <Segmented 
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: 'Overview', value: 'overview' },
                  { label: 'Trends', value: 'trends' },
                  { label: 'Services', value: 'services' }
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <span>Forecast:</span>
              <Switch checked={showForecast} onChange={setShowForecast} />
              {showForecast && (
                <Select value={forecastDays} onChange={setForecastDays} style={{ width: 80 }}>
                  <Option value={7}>7d</Option>
                  <Option value={30}>30d</Option>
                  <Option value={90}>90d</Option>
                </Select>
              )}
            </Space>
          </Col>
          <Col>
            <Button onClick={fetchHistoricalData} loading={loading}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert 
          message="Error" 
          description={error} 
          type="error" 
          closable 
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" tip="Loading historical cost data..." />
        </div>
      )}

      {/* Summary Statistics */}
      {!loading && chartData.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic 
                title="Total Cost" 
                value={summaryStats.totalCost} 
                precision={4}
                prefix="$"
                suffix={
                  <Tooltip title="Cost trend">
                    {renderTrendIndicator(summaryStats.trend)}
                  </Tooltip>
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic 
                title="Videos Processed" 
                value={summaryStats.totalVideos}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic 
                title="Avg Cost per Video" 
                value={summaryStats.avgCostPerVideo} 
                precision={4}
                prefix="$"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic 
                title={`Avg ${granularity === 'daily' ? 'Daily' : 'Hourly'} Cost`}
                value={summaryStats.avgDailyCost} 
                precision={4}
                prefix="$"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Main Content */}
      {!loading && chartData.length > 0 && (
        <Row gutter={16}>
          {/* Main Chart */}
          <Col span={showForecast ? 16 : 24}>
            {viewMode === 'overview' && (
              <Card title="Cost Overview" size="small">
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="cost" orientation="left" />
                    <YAxis yAxisId="videos" orientation="right" />
                    <ChartTooltip 
                      formatter={(value, name) => {
                        if (name.includes('Cost')) return [`$${value.toFixed(4)}`, name];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Area 
                      yAxisId="cost"
                      type="monotone" 
                      dataKey="totalCost" 
                      fill="#1890ff" 
                      fillOpacity={0.6}
                      stroke="#1890ff"
                      name="Total Cost"
                    />
                    <Bar 
                      yAxisId="videos"
                      dataKey="videosProcessed" 
                      fill="#52c41a"
                      name="Videos Processed"
                    />
                    <Line 
                      yAxisId="cost"
                      type="monotone" 
                      dataKey="costPerVideo" 
                      stroke="#ff7875"
                      name="Cost per Video"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            )}
            
            {viewMode === 'services' && renderServiceCostChart()}
            
            {viewMode === 'trends' && renderTrendsAnalysis()}
          </Col>
          
          {/* Forecast */}
          {showForecast && (
            <Col span={8}>
              {renderForecastChart()}
            </Col>
          )}
        </Row>
      )}

      {/* No Data */}
      {!loading && chartData.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <BarChartOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
            <h3>No Historical Data Available</h3>
            <p>Try adjusting the date range or check if cost tracking is enabled.</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default HistoricalCostDashboard;
